import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Chapter } from "@/components/site/Reveal";

const TABS = [
  {
    id: "variance",
    testid: "dashboard-tab-variance",
    label: "Budget Variance",
    metrics: [
      { label: "Approved Budget", value: "₹42.0 Cr" },
      { label: "Projected Final", value: "₹40.7 Cr" },
      { label: "Variance", value: "−3.1%", accent: true },
    ],
    bars: [
      { label: "Line Production", pct: 78 },
      { label: "Talent & Cast", pct: 92 },
      { label: "Post & VFX", pct: 41 },
      { label: "Music & Sound", pct: 24 },
    ],
  },
  {
    id: "cost",
    testid: "dashboard-tab-cost",
    label: "Cost-to-Complete",
    metrics: [
      { label: "Spent to Date", value: "₹28.4 Cr" },
      { label: "Committed", value: "₹9.1 Cr" },
      { label: "Cost to Complete", value: "₹3.2 Cr", accent: true },
    ],
    bars: [
      { label: "Schedule A — City", pct: 100 },
      { label: "Schedule B — Outdoors", pct: 86 },
      { label: "Schedule C — Studio", pct: 52 },
      { label: "Pickups", pct: 10 },
    ],
  },
  {
    id: "audit",
    testid: "dashboard-tab-audit",
    label: "Audit Trail",
    metrics: [
      { label: "Transactions Tested", value: "4,182" },
      { label: "Exceptions Open", value: "3" },
      { label: "Disallowance Risk", value: "Nil", accent: true },
    ],
    bars: [
      { label: "Vendor Invoices", pct: 96 },
      { label: "Crew Payments", pct: 100 },
      { label: "Location & Permits", pct: 88 },
      { label: "Per Diems", pct: 74 },
    ],
  },
];

export default function Dashboard() {
  const [active, setActive] = useState(0);
  const tab = TABS[active];

  return (
    <section id="dashboard" className="relative bg-ink">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute right-0 top-0 h-[420px] w-[560px] rounded-full bg-gold/[0.05] blur-[140px]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
        <Chapter n="Chapter 06" label="The Control Room" dark />
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <h2 className="text-balance font-serif text-3xl font-medium leading-tight tracking-tight text-ivory sm:text-4xl lg:text-5xl">
                Your production, on one pane of glass.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-base leading-relaxed text-mist sm:text-lg">
                Every VERIVISTA engagement reports into a management cockpit — budget variance,
                cost-to-complete, audit trail and rebate status, reviewed weekly with producers.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.24em] text-mist/70">
                Illustrative preview — sample data
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="lg:col-span-8">
            <div data-testid="dashboard-preview-container" className="border border-white/10 bg-abyss/80 shadow-[0_40px_80px_rgba(0,0,0,0.45)]">
              <div className="flex items-center gap-2 border-b border-white/10 px-6 py-4">
                <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                <span className="h-2.5 w-2.5 rounded-full bg-gold/60" />
                <span className="ml-4 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">VERIVISTA Control Room</span>
              </div>

              <div className="flex flex-wrap gap-1 border-b border-white/10 px-4 py-2">
                {TABS.map((t, i) => (
                  <button
                    key={t.id}
                    data-testid={t.testid}
                    onClick={() => setActive(i)}
                    className={`px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-300 ${
                      active === i ? "bg-gold/10 text-gold" : "text-mist hover:text-ivory"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={tab.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35 }}
                  className="p-6 sm:p-8"
                >
                  <div className="grid grid-cols-3 gap-4">
                    {tab.metrics.map((m) => (
                      <div key={m.label} className="border border-white/10 bg-ink/60 p-4 sm:p-5">
                        <p className={`font-serif text-lg sm:text-2xl ${m.accent ? "text-gold" : "text-ivory"}`}>{m.value}</p>
                        <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.16em] text-mist">{m.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 space-y-5">
                    {tab.bars.map((b, i) => (
                      <div key={b.label}>
                        <div className="mb-2 flex items-center justify-between">
                          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-mist">{b.label}</span>
                          <span className="font-mono text-[10px] text-gold">{b.pct}%</span>
                        </div>
                        <div className="h-[3px] w-full bg-white/10">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${b.pct}%` }}
                            transition={{ duration: 0.9, delay: 0.08 * i, ease: [0.22, 1, 0.36, 1] }}
                            className="h-full bg-gradient-to-r from-gold/60 to-gold"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
